import useSWR, { mutate as globalMutate } from "swr";
import { useAuth, useOrganizationList } from "@clerk/nextjs";
import { createFetcher } from "@/lib/api";

interface Organization {
  id: string;
  name: string;
  slug: string;
  clerk_org_id?: string;
}

interface OrgsResponse {
  organizations: Organization[];
  current_org_id: string;
}

export function useOrganizations() {
  const { getToken } = useAuth();
  const { setActive } = useOrganizationList();
  const fetcher = createFetcher(getToken);

  const { data, error, isLoading } = useSWR<OrgsResponse>(
    "/api/v1/organizations",
    fetcher,
    { revalidateOnFocus: false },
  );

  const switchOrg = async (orgId: string) => {
    const org = data?.organizations.find((o) => o.id === orgId);
    const clerkOrgId = org?.clerk_org_id;

    if (clerkOrgId && setActive) {
      await setActive({ organization: clerkOrgId });
    }

    const token = await getToken();
    const res = await fetch("/api/v1/organizations/switch", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ org_id: orgId }),
    });
    if (!res.ok) throw new Error("Failed to switch organization");
    await globalMutate(() => true, undefined, { revalidate: true });
    return res.json();
  };

  return {
    organizations: data?.organizations ?? [],
    currentOrgId: data?.current_org_id ?? "",
    isLoading,
    error,
    switchOrg,
  };
}
